/**
 * Ovládací panel verzí — směry toku, ne hromada karet.
 *
 * Shora dolů: internet (veřejná adresa) → construction page → ostrý slot.
 * Páska: náhled → ostrá → předchozí. Zespodu GitLab do náhledu, DEV ↔ GitLab.
 */
import { type ReactNode, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  ExternalLink,
  FastForward,
  Globe,
  Loader2,
  Lock,
  Monitor,
  Pencil,
  RefreshCw,
} from 'lucide-react';
import { api } from '../lib/api';
import { refreshCodableWorkspace } from '../lib/codable-workspace-refresh';
import { themedConfirm } from '../lib/themed-dialogs';
import { cn } from '../lib/utils';
import { Button, StatusDot } from './ui';

export type CarouselTopology = 'beta' | 'simple' | 'normal' | 'full_split';

type CallTool = (
  name: string,
  args: Record<string, unknown>,
  opts?: { silent?: boolean; label?: string },
) => Promise<unknown>;

export interface CarouselState {
  ok?: boolean;
  live?: string | null;
  preview?: string | null;
  reserved?: string | null;
  reserved_is_previous?: boolean;
  releases?: string[];
  preview_is_live?: boolean;
  layout?: boolean;
  empty_reason?: 'dev_server' | 'not_deployed' | null;
  urls?: { live?: string | null; preview?: string | null };
  rollback?: string | null;
  switched?: { from?: string | null; to?: string };
  error?: string;
}

export interface TapePublic {
  url?: string | null;
  domain?: string | null;
  onHide?: () => void;
  hidePending?: boolean;
  internal?: boolean;
  apexWarn?: boolean;
  onChangeDomain?: (domain: string) => void | Promise<void>;
  changePending?: boolean;
}

export interface TapePath {
  devUrl?: string | null;
  gitlabUrl?: string | null;
  gitlabPath?: string | null;
  sha?: string | null;
  /** Aktuální HEAD workspace a nezapsané změny. Karta DEV náhled. */
  devDetail?: string | null;
  onPull?: () => void;
  pullPending?: boolean;
  onPush?: () => void;
  pushPending?: boolean;
  onFeed: (sha: string) => void;
  feedPending?: boolean;
  feedDisabled?: boolean;
}

const COLS =
  'grid grid-cols-[minmax(0,1fr)_2.5rem_minmax(0,1.25fr)_2.5rem_minmax(0,1fr)] gap-x-1';

function shortId(id: string | null | undefined): string {
  if (!id) return '—';
  return id.length > 12 ? id.slice(0, 12) : id;
}

function hostOf(url: string): string {
  return url.replace(/^https?:\/\//, '');
}

function slotActive(
  topology: CarouselTopology,
  kind: 'preview' | 'live' | 'reserved',
): boolean {
  if (topology === 'beta') return false;
  if (topology === 'simple') return kind === 'live';
  return true;
}

export default function ReleaseCarousel({
  tool = 'apphost.release_switch',
  args,
  compact = false,
  callTool,
  liveUrl: liveUrlProp,
  previewUrl: previewUrlProp,
  topology = 'normal',
  publicHead,
  path,
  between,
}: {
  tool?: string;
  args: Record<string, unknown>;
  compact?: boolean;
  callTool?: CallTool;
  liveUrl?: string | null;
  previewUrl?: string | null;
  topology?: CarouselTopology;
  publicHead?: TapePublic | null;
  path?: TapePath | null;
  between?: ReactNode;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const call: CallTool = callTool ?? ((name, a, opts) => api.mcp(name, a, opts));
  const key = ['apphost-release-carousel', tool, JSON.stringify(args)];
  const [rollbackTo, setRollbackTo] = useState<string | null>(null);
  const [selectedSha, setSelectedSha] = useState(path?.sha ?? '');
  const [editingDomain, setEditingDomain] = useState(false);
  const [domainDraft, setDomainDraft] = useState('');
  const [watchSha, setWatchSha] = useState<string | null>(null);

  const commitsQ = useQuery({
    queryKey: ['gitlab-commits-tape', path?.gitlabPath],
    enabled: Boolean(path?.gitlabPath) && !compact,
    queryFn: () =>
      api.mcp<{
        ok?: boolean;
        commits?: Array<{ id: string; short_id?: string; title?: string }>;
      }>('gitlab.commits', {
        action: 'list',
        project: path!.gitlabPath,
        per_page: 20,
      }, { silent: true }),
    staleTime: 30_000,
  });
  const commits = commitsQ.data?.commits ?? [];

  useEffect(() => {
    if (path?.sha) setSelectedSha(path.sha);
  }, [path?.sha]);
  useEffect(() => {
    if (selectedSha || !commits.length) return;
    const current = path?.sha;
    const hit = current
      ? commits.find((c) => c.id.startsWith(current) || (c.short_id ?? '').startsWith(current))
      : commits[0];
    setSelectedSha(hit?.id ?? commits[0]?.id ?? '');
  }, [commits, path?.sha, selectedSha]);

  const q = useQuery({
    queryKey: key,
    queryFn: () => call(tool, { ...args }, { silent: true }) as Promise<CarouselState>,
    enabled: Object.keys(args).length > 0,
    staleTime: 8_000,
    retry: 1,
  });

  const switchMut = useMutation({
    mutationFn: (body: { to?: string }) =>
      call(tool, {
        ...args,
        slot: 'live',
        confirm: true,
        ...(body.to ? { to: body.to } : {}),
      }, { label: t('releaseCarousel.switching', 'Přepínám ostrou adresu') }) as Promise<
        CarouselState
      >,
    onSuccess: (r) => {
      if (r?.ok === false) {
        toast.error(r.error || t('releaseCarousel.failed', 'Přepnutí selhalo'));
        return;
      }
      if (r.switched?.from) setRollbackTo(r.switched.from);
      toast.success(
        t('releaseCarousel.switched', 'Ostrá adresa teď jede na {{to}}', {
          to: shortId(r.switched?.to ?? r.live),
        }),
      );
      qc.setQueryData(key, (prev: CarouselState | undefined) => ({ ...(prev ?? {}), ...r }));
      refreshCodableWorkspace(qc);
      void qc.invalidateQueries({ queryKey: key });
    },
    onError: (e: unknown) => toast.error(String((e as Error).message ?? e)),
  });

  const previewMut = useMutation({
    mutationFn: (id: string) =>
      call(tool, { ...args, slot: 'preview', to: id }, { silent: true }) as Promise<
        CarouselState
      >,
    onSuccess: (r) => {
      if (r?.ok === false) {
        toast.error(r.error || t('releaseCarousel.previewFailed', 'Náhled se přepnout nepovedlo'));
        return;
      }
      toast.success(
        t(
          'releaseCarousel.previewNow',
          'Náhled jede na {{to}} — ostrá adresa se nedotkla',
          { to: shortId(r.switched?.to ?? r.preview) },
        ),
      );
      qc.setQueryData(key, (prev: CarouselState | undefined) => ({ ...(prev ?? {}), ...r }));
      refreshCodableWorkspace(qc);
      void qc.invalidateQueries({ queryKey: key });
    },
    onError: (e: unknown) => toast.error(String((e as Error).message ?? e)),
  });

  const live = q.data?.live ?? null;
  const confirmLiveSwitch = async (to: string) => {
    const ok = await themedConfirm({
      title: t('releaseCarousel.liveSwitchTitle', 'Přepnutí ostré adresy'),
      description: t(
        'releaseCarousel.confirmLive',
        'Ostrá adresa pojede z {{from}} na {{to}}.',
        { from: shortId(live), to: shortId(to) },
      ) + ' ' + t('releaseCarousel.confirmPublic', 'Přepnutím tohle uvidí veřejnost.'),
      danger: true,
      confirmLabel: t('releaseCarousel.promote', 'Přepnout na ostrou'),
    });
    if (!ok) return;
    switchMut.mutate({ to });
  };

  const hidePublic = async () => {
    if (!publicHead?.onHide) return;
    const ok = await themedConfirm({
      title: t('ui.hide', 'Skrýt'),
      description: t(
        'releaseCarousel.hidePublic',
        'Veřejná adresa zmizí z internetu. Ostrá verze v karuselu zůstane.',
      ),
      danger: true,
      confirmLabel: t('ui.hide', 'Skrýt'),
    });
    if (ok) publicHead.onHide();
  };

  if (q.isError || (q.data && q.data.ok === false)) {
    return (
      <div className='rounded-lg border border-red-900/40 bg-gray-900/30 px-3 py-2 text-xs text-red-300'>
        {q.data?.error || String((q.error as Error)?.message ?? q.error) ||
          t('releaseCarousel.readFailed', 'Stav verzí se nepovedlo načíst')}
      </div>
    );
  }

  if (q.isLoading) {
    return (
      <div className='flex items-center gap-2 py-2 text-xs text-gray-500'>
        <Loader2 size={14} className='animate-spin' />
        {t('releaseCarousel.loading', 'Načítám verze…')}
      </div>
    );
  }

  const preview = q.data?.preview ?? null;
  const reserved = q.data?.reserved ?? rollbackTo ?? null;
  const same = q.data?.preview_is_live === true;
  const liveUrl = q.data?.urls?.live ?? liveUrlProp ?? publicHead?.url ?? null;
  const previewUrl = q.data?.urls?.preview ?? previewUrlProp ?? null;
  const publicHref = liveUrl ??
    (publicHead?.domain ? `https://${publicHead.domain}` : null);
  const busy = switchMut.isPending;
  const previewOn = slotActive(topology, 'preview');
  const liveOn = slotActive(topology, 'live');
  const reservedOn = slotActive(topology, 'reserved');
  const canShift = previewOn && liveOn && Boolean(preview) && !same && !busy;
  const canRewind = reservedOn && liveOn && Boolean(reserved) && !busy;
  const feedTarget = previewOn ? 'preview' : liveOn ? 'live' : null;
  const canFeed = Boolean(path) && feedTarget !== null && !path?.feedDisabled;
  const isBeta = topology === 'beta';
  const isSimple = topology === 'simple';
  const showTape = !isBeta;
  const showPreview = topology === 'normal' || topology === 'full_split';
  const feedSha = selectedSha || path?.sha || '';
  const feedLabel = feedTarget === 'live'
    ? t('releaseCarousel.feedLiveSha', 'Nasadit GitLab {{sha}} na ostrou', {
      sha: shortId(feedSha),
    })
    : t('releaseCarousel.feedPreviewSha', 'Nasadit {{sha}} do náhledu', {
      sha: shortId(feedSha),
    });

  return (
    <div className={cn(COLS, 'items-stretch gap-y-1')}>
        <div className='col-span-5 rounded-lg border border-gray-700 bg-gray-900/50 px-3 py-2'>
          <div className='flex items-start gap-2'>
            <Globe size={14} className='mt-0.5 shrink-0 text-blue-400' />
            <div className='min-w-0 flex-1'>
              <p className='text-[9px] font-semibold uppercase tracking-[0.16em] text-gray-500'>
                {t('releaseCarousel.publicUrl', 'Veřejná adresa')}
              </p>
              {publicHref
                ? (
                  <a
                    href={publicHref}
                    target='_blank'
                    rel='noreferrer'
                    className='flex items-center gap-1 font-mono text-sm text-blue-300 hover:text-blue-200'
                    title={publicHref}
                  >
                    <span className='truncate'>{hostOf(publicHref)}</span>
                    <ExternalLink size={11} className='shrink-0' />
                  </a>
                )
                : (
                  <p className='text-xs text-gray-600'>
                    {t('releaseCarousel.publicUrlMissing', 'produkční adresa zatím není')}
                  </p>
                )}
            </div>
            <div className='flex shrink-0 items-center gap-1'>
              {publicHead?.onChangeDomain && (
                <Button
                  size='sm'
                  variant='ghost'
                  loading={publicHead.changePending}
                  onClick={() => {
                    setDomainDraft(hostOf(publicHref ?? '') || publicHead.domain || '');
                    setEditingDomain((v) => !v);
                  }}
                >
                  <Pencil size={11} />
                </Button>
              )}
              {publicHead?.onHide && publicHref && (
                <Button
                  size='sm'
                  variant='ghost'
                  loading={publicHead.hidePending}
                  onClick={() => void hidePublic()}
                >
                  <Lock size={11} />
                  {t('ui.hide', 'Skrýt')}
                </Button>
              )}
            </div>
          </div>
          {editingDomain && publicHead?.onChangeDomain && (
            <form
              className='mt-2 flex gap-2'
              onSubmit={(e) => {
                e.preventDefault();
                const next = domainDraft.trim().replace(/^https?:\/\//, '');
                if (!next) return;
                void Promise.resolve(publicHead.onChangeDomain!(next)).then(() =>
                  setEditingDomain(false)
                );
              }}
            >
              <input
                value={domainDraft}
                onChange={(e) => setDomainDraft(e.target.value)}
                className='h-8 min-w-0 flex-1 rounded-lg border border-gray-700 bg-gray-950 px-2 font-mono text-xs text-gray-200'
                placeholder='app.example.com'
                autoFocus
              />
              <Button size='sm' type='submit' loading={publicHead.changePending}>
                {t('common.save', 'Uložit')}
              </Button>
            </form>
          )}
          {publicHead?.internal && (
            <p className='mt-1.5 text-[10px] leading-snug text-amber-300/90'>
              {t(
                'codable.wsInternalDomain',
                'Interní auto-generovaná adresa, ne vlastní doména zákazníka.',
              )}
            </p>
          )}
          {publicHead?.apexWarn && (
            <p className='mt-1 text-[10px] leading-snug text-amber-300/90'>
              {t(
                'codable.wsApexDomainWarn',
                'Doména druhého řádu bez www — vědomé rozhodnutí.',
              )}
            </p>
          )}
        </div>

        <div className='col-span-5 text-[11px] leading-snug text-gray-400'>
          {isBeta
            ? t('releaseCarousel.pathBeta', 'Platná cesta: DEV ↔ GitLab. Beta nemá veřejnou produkci.')
            : isSimple
            ? t(
              'releaseCarousel.pathSimple',
              'Platná cesta: GitLab → ostrá. DEV a veřejná adresa jsou jedna FE instance.',
            )
            : topology === 'full_split'
            ? t(
              'releaseCarousel.pathFullSplit',
              'Platná cesta: GitLab → náhled → ostrá. DEV i backend jsou zvlášť.',
            )
            : t(
              'releaseCarousel.pathNormal',
              'Platná cesta: GitLab → náhled → ostrá. DEV je zvlášť.',
            )}
        </div>

        {between && <div className='col-span-5'>{between}</div>}
        {!isBeta && (
          <>
            <div />
            <div />
            <FlowDown
              title={t('releaseCarousel.liveHint', 'tady stojí produkce')}
            />
            <div />
            <div />
          </>
        )}

        {showTape && (
          <>
            {showPreview
              ? (
                <Slot
                  kind='preview'
                  id={preview}
                  waiting={previewOn && !same}
                  active={previewOn}
                  url={previewOn ? previewUrl : null}
                  hint={t('releaseCarousel.previewHint', 'sem přijede z GitLabu')}
                  empty={!previewUrl
                    ? t(
                      'releaseCarousel.previewAddressOff',
                      'Veřejná adresa náhledu není zapnutá.',
                    )
                    : null}
                />
              )
              : <div />}
            {showPreview
              ? (
                <TapeBtn
                  axis='right'
                  enabled={canShift}
                  busy={busy}
                  label={t('releaseCarousel.shift', 'Posunout na ostrou')}
                  onClick={() => {
                    if (preview) void confirmLiveSwitch(preview);
                  }}
                />
              )
              : <div />}
            <Slot
              kind='live'
              id={live}
              current={liveOn}
              active={liveOn}
              url={liveOn ? liveUrl : null}
              hint={isSimple
                ? t('releaseCarousel.liveHintSimple', 'veřejná i DEV na jedné FE')
                : t('releaseCarousel.liveHint', 'tady stojí produkce')}
            />
            {showPreview
              ? (
                <TapeBtn
                  axis='left'
                  enabled={canRewind}
                  busy={busy}
                  label={t('releaseCarousel.rewind', 'Rewind — vrátit ostrou')}
                  onClick={() => {
                    if (reserved) void confirmLiveSwitch(reserved);
                  }}
                />
              )
              : <div />}
            {showPreview
              ? (
                <Slot
                  kind='reserved'
                  id={reserved}
                  active={reservedOn}
                  url={reservedOn && reserved && previewUrl ? previewUrl : null}
                  onOpen={reserved && previewOn && previewUrl
                    ? () => previewMut.mutate(reserved)
                    : undefined}
                  hint={t('releaseCarousel.reservedHeld', 'starší odchází')}
                  empty={!reserved ? t('releaseCarousel.noRollback', 'prázdné') : null}
                />
              )
              : <div />}
          </>
        )}

      {path && !compact && (
        <>
          {showPreview
            ? (
              <TapeBtn
                axis='up'
                enabled={canFeed}
                busy={!!path.feedPending}
                label={feedLabel}
                onClick={() => {
                  setWatchSha(feedSha);
                  path.onFeed(feedSha);
                }}
              />
            )
            : <div />}
          <div />
          {isSimple
            ? (
              <TapeBtn
                axis='up'
                enabled={canFeed}
                busy={!!path.feedPending}
                label={feedLabel}
                onClick={() => {
                  setWatchSha(feedSha);
                  path.onFeed(feedSha);
                }}
              />
            )
            : <div />}
          <div />
          <div />
          <div className='col-span-5 overflow-hidden rounded-lg border border-[#e24329]/30 bg-gray-950/70'>
            <div className='flex h-7 items-center gap-2 border-b border-[#e24329]/20 bg-[#e24329]/10 px-2.5'>
              <GitlabLogo />
              <span className='truncate text-xs font-semibold text-gray-100'>
                {gitlabProjectName(path.gitlabPath)}
              </span>
            </div>
            <div className='space-y-2.5 px-3 py-2.5'>
              {path.gitlabUrl
                ? (
                  <a
                    href={path.gitlabUrl}
                    target='_blank'
                    rel='noreferrer'
                    className='flex items-center gap-1.5 font-mono text-sm text-blue-300 hover:text-blue-200'
                    title={path.gitlabUrl}
                  >
                    <span className='min-w-0 truncate'>{hostOf(path.gitlabUrl)}</span>
                    <ExternalLink size={12} className='shrink-0' />
                  </a>
                )
                : path.gitlabPath
                ? (
                  <p className='truncate font-mono text-sm text-blue-300'>{path.gitlabPath}</p>
                )
                : (
                  <p className='text-sm text-gray-600'>
                    {t('releaseCarousel.gitlabUnknown', 'commit nezjištěn')}
                  </p>
                )}
              <div className='grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(11rem,0.95fr)] sm:items-stretch'>
                <select
                  className='h-8 w-full rounded-lg border border-gray-700 bg-gray-950 px-2 font-mono text-[11px] text-gray-200'
                  value={selectedSha}
                  onChange={(e) => setSelectedSha(e.target.value)}
                  aria-label={t('releaseCarousel.build', 'Build')}
                >
                  {(commits.length
                    ? commits
                    : selectedSha
                    ? [{ id: selectedSha, short_id: selectedSha.slice(0, 8), title: '' }]
                    : []
                  ).map((c) => {
                    const short = c.short_id || c.id.slice(0, 8);
                    const current = path.sha &&
                      (c.id.startsWith(path.sha) || short.startsWith(path.sha));
                    return (
                      <option key={c.id} value={c.id}>
                        {short}
                        {current ? ' · aktuální' : ''}
                        {c.title ? ` · ${c.title.slice(0, 48)}` : ''}
                      </option>
                    );
                  })}
                </select>
                <PipelineWatch
                  project={path.gitlabPath}
                  sha={watchSha || selectedSha}
                  live={!!path.feedPending}
                />
              </div>
            </div>
          </div>
          <div className='col-span-5 flex items-center justify-center gap-3'>
            <TapeBtn
              axis='up'
              enabled={!!path.onPush && !path.pushPending}
              busy={!!path.pushPending}
              label={t('releaseCarousel.pushGitlab', 'Nahrát DEV do GitLabu')}
              onClick={() => path.onPush?.()}
            />
            <TapeBtn
              axis='sync'
              enabled={!!path.onPull && !path.pullPending}
              busy={!!path.pullPending}
              label={t('releaseCarousel.syncGitlab', 'Synchronizovat s GitLabem')}
              onClick={() => path.onPull?.()}
            />
            <TapeBtn
              axis='down'
              enabled={!!path.onPull && !path.pullPending}
              busy={!!path.pullPending}
              label={t('releaseCarousel.pullGitlab', 'Stáhnout do DEV')}
              onClick={() => path.onPull?.()}
            />
          </div>
          <div className='col-span-5'>
            <FlowCard
              icon={<Monitor size={13} className='text-blue-400' />}
              kicker={t('codable.wsDevPreview', 'DEV náhled')}
              title={path.devUrl ? hostOf(path.devUrl) : t('codable.wsDevUrlUnknown', 'DEV URL není známá')}
              href={path.devUrl}
              detail={path.devDetail
                || t('releaseCarousel.devHint', 'živý workspace — sem se stahuje GitLab')}
            />
          </div>
        </>
      )}


    </div>
  );
}

function gitlabProjectName(path?: string | null): string {
  const p = (path ?? '').replace(/\/+$/, '');
  if (!p) return 'GitLab';
  return p.split('/').filter(Boolean).pop() ?? p;
}

function pipelineDotStatus(status: string): string {
  const s = status.toLowerCase();
  if (s === 'success' || s === 'manual') return 'running';
  if (s === 'running' || s === 'pending' || s === 'created' || s === 'preparing' ||
    s === 'waiting_for_resource' || s === 'scheduled')
  {
    return 'pending';
  }
  if (s === 'failed' || s === 'canceled' || s === 'cancelled' || s === 'skipped') return 'failed';
  return s;
}

function PipelineWatch({
  project,
  sha,
  live,
}: {
  project?: string | null;
  sha: string;
  live: boolean;
}) {
  const { t } = useTranslation();
  const runningish = (st: string) =>
    ['running', 'pending', 'created', 'preparing', 'waiting_for_resource', 'scheduled']
      .includes(st);

  const pipeQ = useQuery({
    queryKey: ['gitlab-pipelines-tape', project, sha],
    enabled: Boolean(project) && Boolean(sha),
    queryFn: () =>
      api.mcp<{
        ok?: boolean;
        pipelines?: Array<{
          id: number;
          status: string;
          sha?: string;
          web_url?: string;
          ref?: string;
        }>;
      }>('gitlab.pipelines_list', {
        project,
        sha,
        per_page: 5,
      }, { silent: true }),
    refetchInterval: (q) => {
      const st = q.state.data?.pipelines?.[0]?.status ?? '';
      if (live || runningish(st)) return 4000;
      return false;
    },
    staleTime: 8_000,
  });

  const pipe = pipeQ.data?.pipelines?.[0] ?? null;
  const jobsQ = useQuery({
    queryKey: ['gitlab-pipeline-jobs-tape', project, pipe?.id],
    enabled: Boolean(project) && Boolean(pipe?.id),
    queryFn: () =>
      api.mcp<{
        ok?: boolean;
        jobs?: Array<{ id: number; name: string; status: string; failure_code?: string; web_url?: string }>;
      }>('gitlab.pipeline_jobs', {
        project,
        pipeline_id: pipe!.id,
      }, { silent: true }),
    refetchInterval: live || (pipe && runningish(pipe.status)) ? 4000 : false,
    staleTime: 8_000,
  });
  const jobs = jobsQ.data?.jobs ?? [];
  const status = pipe?.status ?? '';
  const label = !sha
    ? t('releaseCarousel.pipelineNone', 'CI staví při pushi, ne při nasazení.')
    : pipeQ.isLoading
    ? t('releaseCarousel.loading', 'Načítám verze…')
    : !pipe
    ? t('releaseCarousel.pipelineNone', 'CI staví při pushi, ne při nasazení.')
    : status === 'success'
    ? t('releaseCarousel.pipelineSuccess', 'Artefakt hotový')
    : runningish(status)
    ? t('releaseCarousel.pipelineRunning', 'Pipeline běží')
    : status === 'failed'
    ? t('releaseCarousel.pipelineFailed', 'Pipeline selhala')
    : status;

  return (
    <div
      className={cn(
        'flex min-h-8 flex-col justify-center rounded-lg border px-2 py-1',
        live || runningish(status)
          ? 'border-blue-500/35 bg-blue-500/5'
          : status === 'success'
          ? 'border-emerald-500/25 bg-emerald-500/5'
          : status === 'failed'
          ? 'border-red-500/25 bg-red-500/5'
          : 'border-gray-800 bg-gray-950/50',
      )}
    >
      <div className='flex items-center gap-1.5'>
        {(pipeQ.isFetching || live) && !pipe
          ? <Loader2 size={12} className='shrink-0 animate-spin text-blue-300' />
          : <StatusDot status={pipelineDotStatus(status || 'offline')} />}
        {pipe?.web_url
          ? (
            <a
              href={pipe.web_url}
              target='_blank'
              rel='noreferrer'
              className='min-w-0 truncate text-[10px] text-gray-200 hover:text-white'
              title={label}
            >
              {label}
            </a>
          )
          : (
            <span className='min-w-0 truncate text-[10px] text-gray-400' title={label}>
              {label}
            </span>
          )}
      </div>
      {jobs.length > 0 && (
        <div className='mt-1 flex flex-wrap gap-1'>
          {jobs.slice(0, 8).map((j) => (
            <span key={j.id} title={`${j.name}: ${j.status}`}>
              <StatusDot status={pipelineDotStatus(j.status)} />
            </span>
          ))}
        </div>
      )}
      {jobs.some((j) => j.failure_code === 'ARTIFACT_TOO_LARGE') && (
        <p role='alert' className='mt-1 text-xs text-red-300'>
          {t('releaseCarousel.artifactTooLarge', 'GitLab odmítl artefakt: překročený limit velikosti (413). Zmenši artefakt nebo uprav limit GitLabu/proxy a zopakuj build.')}
        </p>
      )}
      {(live || runningish(status)) && (
        <div className='mt-1 h-0.5 overflow-hidden rounded bg-blue-500/20'>
          <div className='h-full w-2/3 animate-pulse rounded bg-blue-400/80' />
        </div>
      )}
    </div>
  );
}

function GitlabLogo() {
  return (
    <svg viewBox='0 0 36 36' className='h-7 w-7 shrink-0' aria-hidden>
      <path fill='#E24329' d='M18 34.5 3.9 19.7a.7.7 0 0 1-.2-.8L8.1 6.4a.7.7 0 0 1 1.3 0l3 9.3h11.2l3-9.3a.7.7 0 0 1 1.3 0l4.4 12.5a.7.7 0 0 1-.2.8z' />
      <path fill='#FC6D26' d='M18 34.5 13.2 19.7H8.1z' />
      <path fill='#FCA326' d='M18 34.5 22.8 19.7h5.1z' />
      <path fill='#FCA326' d='M8.1 19.7 3.9 19.7 18 34.5z' />
      <path fill='#E24329' d='M27.9 19.7h4.2L18 34.5z' />
      <path fill='#FC6D26' d='M8.1 19.7h5.1L18 34.5zM22.8 19.7h5.1L18 34.5z' />
    </svg>
  );
}

function FlowCard({
  icon,
  kicker,
  title,
  href,
  detail,
  action,
}: {
  icon: ReactNode;
  kicker: string;
  title: string;
  href?: string | null;
  detail: string;
  action?: ReactNode;
}) {
  return (
    <div className='w-full rounded-lg border border-gray-800 bg-gray-900/40 px-2.5 py-2'>
      <div className='flex items-start gap-2'>
        <span className='mt-0.5 shrink-0'>{icon}</span>
        <div className='min-w-0 flex-1'>
          <p className='text-[9px] font-semibold uppercase tracking-[0.14em] text-gray-500'>
            {kicker}
          </p>
          {href
            ? (
              <a
                href={href}
                target='_blank'
                rel='noreferrer'
                className='block truncate font-mono text-[11px] text-blue-300 hover:text-blue-200'
                title={href}
              >
                {title}
              </a>
            )
            : <p className='truncate font-mono text-[11px] text-gray-300'>{title}</p>}
          <p className='text-[10px] text-gray-500'>{detail}</p>
        </div>
        {action}
      </div>
    </div>
  );
}

function FlowDown({ title }: { title: string }) {
  return (
    <div className='flex items-center justify-center' aria-hidden>
      <span
        title={title}
        className='flex h-9 w-9 items-center justify-center rounded-lg border border-blue-500/35 bg-gray-900/80 text-blue-300'
      >
        <FastForward size={16} className='rotate-90' />
      </span>
    </div>
  );
}

function TapeBtn({
  axis,
  enabled,
  busy,
  label,
  onClick,
}: {
  axis: 'right' | 'left' | 'up' | 'down' | 'sync';
  enabled: boolean;
  busy: boolean;
  label: string;
  onClick: () => void;
}) {
  const rot = axis === 'up'
    ? '-rotate-90'
    : axis === 'down'
    ? 'rotate-90'
    : axis === 'left'
    ? 'rotate-180'
    : '';
  return (
    <div className='flex items-center justify-center'>
      <button
        type='button'
        disabled={!enabled || busy}
        onClick={onClick}
        title={label}
        aria-label={label}
        className={cn(
          'flex h-9 w-9 items-center justify-center rounded-lg border transition-colors',
          enabled
            ? 'border-blue-500/35 bg-gray-900/80 text-blue-300 hover:border-blue-400/70 hover:bg-blue-500/10 hover:text-blue-200'
            : 'cursor-default border-gray-800 bg-gray-900/20 text-gray-600',
        )}
      >
        {busy
          ? <Loader2 size={16} className='animate-spin' />
          : axis === 'sync'
          ? <RefreshCw size={16} />
          : <FastForward size={16} className={rot} />}
      </button>
    </div>
  );
}

function Slot({
  kind,
  id,
  current,
  waiting,
  active,
  hint,
  empty,
  action,
  url,
  onOpen,
}: {
  kind: 'reserved' | 'live' | 'preview';
  id: string | null;
  current?: boolean;
  waiting?: boolean;
  active: boolean;
  hint: string;
  empty?: string | null;
  action?: ReactNode;
  url?: string | null;
  onOpen?: () => void;
}) {
  const { t } = useTranslation();
  const label = kind === 'live'
    ? t('releaseCarousel.live', 'Ostrá')
    : kind === 'preview'
    ? t('releaseCarousel.preview', 'Náhled')
    : t('releaseCarousel.reserved', 'Předchozí');
  const href = url && !onOpen ? url : null;
  return (
    <div
      className={cn(
        'min-w-0 overflow-hidden rounded-lg border',
        !active && 'opacity-40',
        current
          ? 'border-blue-500/40 bg-gray-950/80'
          : waiting
          ? 'border-amber-500/30 bg-gray-950/70'
          : 'border-gray-800 bg-gray-950/60',
      )}
    >
      <div
        className={cn(
          'flex h-7 items-center border-b px-2.5',
          current
            ? 'border-blue-500/20 bg-blue-500/10'
            : waiting
            ? 'border-amber-500/20 bg-amber-500/5'
            : 'border-gray-800 bg-gray-900/80',
        )}
      >
        <span
          className={cn(
            'text-[10px] font-semibold uppercase tracking-wide',
            current ? 'text-blue-300' : waiting ? 'text-amber-300' : 'text-gray-400',
          )}
        >
          {label}
        </span>
      </div>
      <div className='flex flex-col gap-1 px-2.5 py-2'>
        <div className='truncate font-mono text-xs text-gray-100' title={id ?? ''}>
          {shortId(id)}
        </div>
        {href && (
          <a
            href={href}
            target='_blank'
            rel='noreferrer'
            className='flex items-center gap-1 truncate font-mono text-[11px] text-blue-300 hover:text-blue-200'
            title={href}
          >
            <span className='truncate'>{hostOf(href)}</span>
            <ExternalLink size={10} className='shrink-0' />
          </a>
        )}
        {onOpen && url && (
          <button
            type='button'
            onClick={onOpen}
            className='flex items-center gap-1 truncate text-left font-mono text-[11px] text-blue-300 hover:text-blue-200'
            title={url}
          >
            <span className='truncate'>{hostOf(url)}</span>
            <ExternalLink size={10} className='shrink-0' />
          </button>
        )}
        <div className='text-[10px] leading-snug text-gray-500'>{empty || hint}</div>
        {action && active && <div className='pt-0.5'>{action}</div>}
      </div>
    </div>
  );
}
